import { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { resetPassword } from '../../services/authService'
import { apiError } from '../../../admin/api/apiError'
import AuthCard from '../../components/AuthCard/AuthCard'

const MIN_PASSWORD_LENGTH = 8

type Mode = 'reset' | 'set'
type LinkProblem = 'TOKEN_INVALID' | 'TOKEN_EXPIRED'

// один экран на два сценария: сброс пароля (/reset-password) и первичная установка по приглашению (/set-password)
export default function ResetPasswordPage({ mode }: { mode: Mode }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const token = new URLSearchParams(location.search).get('token') ?? ''

  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const [confirmError, setConfirmError] = useState('')
  const [error, setError] = useState('')
  const [linkProblem, setLinkProblem] = useState<LinkProblem | null>(token ? null : 'TOKEN_INVALID')
  const [loading, setLoading] = useState(false)

  const prefix = `dealer.${mode === 'set' ? 'set_password' : 'reset_password'}`

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    let valid = true
    if (password.length < MIN_PASSWORD_LENGTH) {
      setPasswordError(t('dealer.password.error_too_short', { min: MIN_PASSWORD_LENGTH }))
      valid = false
    }
    if (confirm !== password) {
      setConfirmError(t('dealer.password.error_mismatch'))
      valid = false
    }
    if (!valid) return

    setError('')
    setLoading(true)
    try {
      await resetPassword({ token, password })
      navigate(`/login?notice=${mode === 'set' ? 'password_set' : 'password_reset'}`, { replace: true })
    } catch (err) {
      const { status, code } = apiError(err)
      if (code === 'TOKEN_INVALID' || code === 'TOKEN_EXPIRED') setLinkProblem(code)
      else if (code === 'PASSWORD_TOO_SHORT') {
        setPasswordError(t('dealer.password.error_too_short', { min: MIN_PASSWORD_LENGTH }))
      } else if (!status) setError(t('dealer.common.error_network'))
      else setError(t('dealer.common.error_server'))
      console.error('[ERROR] Dealer reset-password failed:', err)
    } finally {
      setLoading(false)
    }
  }

  const footer = (
    <Link to="/login" className="dealer-link">{t('dealer.common.back_to_login')}</Link>
  )

  if (linkProblem) {
    const problemKey = linkProblem === 'TOKEN_EXPIRED' ? 'expired' : 'invalid'
    return (
      <AuthCard title={t(`${prefix}.link_${problemKey}_title`)} footer={footer}>
        <div className="dealer-alert dealer-alert--error">{t(`${prefix}.link_${problemKey}_text`)}</div>
        <Link to="/forgot-password" className="dealer-btn dealer-btn--primary dealer-btn--block">
          {t('dealer.password.request_new_link')}
        </Link>
      </AuthCard>
    )
  }

  return (
    <AuthCard title={t(`${prefix}.title`)} subtitle={t(`${prefix}.subtitle`)} footer={footer}>
      <form className="dealer-auth__form" onSubmit={handleSubmit} noValidate>
        <div className="dealer-field">
          <label className="dealer-label" htmlFor="dealer-new-password">{t('dealer.password.label_new')}</label>
          <input
            id="dealer-new-password"
            className={`dealer-input${passwordError ? ' dealer-input--error' : ''}`}
            type="password"
            value={password}
            onChange={(e) => { setPassword(e.target.value); setPasswordError(''); setError('') }}
            autoFocus
            autoComplete="new-password"
            disabled={loading}
          />
          {passwordError
            ? <span className="dealer-field-error">{passwordError}</span>
            : <span className="dealer-hint">{t('dealer.password.hint', { min: MIN_PASSWORD_LENGTH })}</span>}
        </div>
        <div className="dealer-field">
          <label className="dealer-label" htmlFor="dealer-confirm-password">{t('dealer.password.label_confirm')}</label>
          <input
            id="dealer-confirm-password"
            className={`dealer-input${confirmError ? ' dealer-input--error' : ''}`}
            type="password"
            value={confirm}
            onChange={(e) => { setConfirm(e.target.value); setConfirmError(''); setError('') }}
            autoComplete="new-password"
            disabled={loading}
          />
          {confirmError && <span className="dealer-field-error">{confirmError}</span>}
        </div>

        {error && <div className="dealer-alert dealer-alert--error" role="alert">{error}</div>}

        <button className="dealer-btn dealer-btn--primary dealer-btn--block" type="submit" disabled={loading}>
          {loading ? t('dealer.password.saving') : t(`${prefix}.submit`)}
        </button>
      </form>
    </AuthCard>
  )
}
